/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    TablePagination,
    Checkbox,
    Typography,
    IconButton,
    Tooltip,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import CircularProgress from '@mui/material/CircularProgress';
import { endPoints } from 'pages/dlops-dashboard/api/endpoint';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from 'components/error-fallback';
import AccessToken from '../../components/accessToken/accessToken';
import { ProgressIcon } from '../../style';
import { StyledTableHeader, StyledTableCell, style } from '../style';
import CardList from '../cardList.js';

const ClusterLabelTable = ({
    setViewMode,
    viewMode,
    driftDataSet,
    selectedIds,
    setSelectedIds,
    selectedCluster,
    setSelectedCluster,
    deleteImage,
    selectedIdsURL,
    setSelectedIdsURL,
    setDeletedImage,
    selectedImageCount,
    setSelectedImageCount,
}) => {
    const { getAccessToken, handleTokenError } = AccessToken();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [startIndex] = useState(0);
    const [endIndex] = useState(6);
    const [tableData, setTableData] = useState([]);
    const [isLoading, setLoading] = useState(true);
    const [cardData, setCardData] = useState([]);
    const [selectedClusterId, setSelectedClusterId] = useState([]);
    const [selectedClusterName, setSelectedClusterName] = useState('');
    const [selectedImageCluster, setSelectedImageCluster] = useState([]);
    const [isCardLoading, setIsCardLoading] = useState(false);
    const [removedClusterIds, setRemovedClusterIds] = useState([]);
    const [manuallyCheckedRows, setManuallyCheckedRows] = useState([]);
    const [boxHeight, setBoxHeight] = useState('40vh');
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const [clusterToDelete, setClusterToDelete] = useState({ id: null, name: '' });

    useEffect(() => {
        const updateHeight = () => {
            const screenWidth = window.innerWidth;
            setBoxHeight(screenWidth > 1440 ? window.innerHeight - 320 : '70vh');
        };

        updateHeight();
        window.addEventListener('resize', updateHeight);

        return () => window.removeEventListener('resize', updateHeight);
    }, []);

    const confirmDeleteCluster = () => {
        if (clusterToDelete.id) {
            const clusterStr = String(clusterToDelete.id);
            setRemovedClusterIds((prev) => [...prev, clusterStr]);
            setSelectedCluster((prev) => prev.filter((id) => id !== clusterStr));
            setManuallyCheckedRows((prev) => prev.filter((id) => id !== clusterStr));
            toast.success(`Cluster "${clusterToDelete.name}" successfully removed`, {
                position: 'top-right',
                autoClose: 3000,
            });
        }
        setDeleteConfirmationOpen(false);
        setClusterToDelete({ id: null, name: '' });
    };

    const handleDeleteCluster = (label, clusterName) => {
        setClusterToDelete({ id: label, name: clusterName });
        setDeleteConfirmationOpen(true);
    };

    const columns = [
        { key: 'label', name: 'Cluster Label' },
        { key: 'cluster_size', name: 'Cluster Size' },
        { key: 'selectedImages', name: 'Selected Images' },
        { key: 'actions', name: '' },
    ];

    useEffect(() => {
        if (selectedImageCluster?.length) {
            const imageClusters = [...new Set(selectedImageCluster.map(String))];
            const mergedClusters = [...new Set([...selectedCluster.map(String), ...imageClusters])];
            setSelectedCluster(mergedClusters);
        }
    }, [selectedImageCluster]);

    useEffect(() => {
        if (driftDataSet?.length) {
            const formattedData = driftDataSet.map((item) => {
                const clusterIdStr = String(item.label);
                const isSelected = selectedCluster.includes(item.label) || selectedCluster.includes(clusterIdStr);
                const hasImageCount = Object.prototype.hasOwnProperty.call(selectedImageCount, clusterIdStr);

                const selectedImageVal = isSelected && !hasImageCount ? item.production : selectedImageCount[clusterIdStr] ?? 0;
                return {
                    label: item.label || `Cluster ${item.label}`,
                    cluster_size: item.cluster_size,
                    production: item.production,
                    selectedImages: selectedImageVal,
                };
            });

            setTableData(formattedData);
        } else {
            setTableData([]);
        }

        setLoading(false);
    }, [driftDataSet, selectedImageCount, selectedCluster]);

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelected = tableData.filter((row) => !removedClusterIds.includes(row.label)).map((row) => String(row.label));
            setSelectedCluster(newSelected);
            setManuallyCheckedRows(newSelected);
        } else {
            setSelectedCluster([]);
            setManuallyCheckedRows([]);
        }
    };

    const handleClick = (label) => {
        const clusterStr = String(label);
        const isSelectedNow = selectedCluster.includes(clusterStr);

        setSelectedCluster((prevSelected) => (isSelectedNow ? prevSelected.filter((id) => id !== clusterStr) : [...prevSelected, clusterStr]));

        setManuallyCheckedRows((prev) => (isSelectedNow ? prev.filter((id) => id !== clusterStr) : [...prev, clusterStr]));
    };

    const isSelected = (label) => selectedCluster.includes(String(label));

    const fetchClusterDetail = async (clusterIdParam, clusterLabelParam) => {
        const driftDataSetString = localStorage.getItem('viewDriftDataSetVersion');
        const driftDataSetStorage = JSON.parse(driftDataSetString);
        const modelId = driftDataSetStorage?.model_id;

        const payload = {
            model_id: modelId,
            cluster_label: clusterLabelParam,
            label: clusterIdParam,
        };

        try {
            setIsCardLoading(true);
            const accessToken = getAccessToken();
            const response = await axios.post(endPoints.getClusterDetails(startIndex, endIndex, modelId), payload, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            setCardData(response?.data?.data);
            setViewMode('cards');
        } catch (error) {
            handleTokenError(error, endPoints.getClusterDetails(startIndex, endIndex, modelId), 'post', payload);
            toast.error('Failed to fetch cluster details. Please try again.');
        } finally {
            setIsCardLoading(false);
        }
    };

    const renderTable = () => (
        <Box>
            <Paper
                sx={{
                    ...style.tablePaper,
                    px: '30px',
                    py: 0,
                    height: boxHeight,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {tableData.length > 0 ? (
                    <>
                        <TableContainer sx={{ flexGrow: 1, overflowY: 'auto' }}>
                            <Table stickyHeader sx={{ minWidth: 650 }}>
                                <TableHead
                                    sx={{
                                        '& .MuiTableCell-root': {
                                            backgroundColor: (themes) => themes.palette.cyan['90'],
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 1,
                                        },
                                    }}
                                >
                                    <TableRow>
                                        <StyledTableHeader padding="checkbox">
                                            <Checkbox
                                                indeterminate={
                                                    selectedCluster.length > 0 &&
                                                    selectedCluster.length < tableData.filter((row) => !removedClusterIds.includes(row.label)).length
                                                }
                                                checked={
                                                    tableData.filter((row) => !removedClusterIds.includes(row.label)).length > 0 &&
                                                    selectedCluster.length === tableData.filter((row) => !removedClusterIds.includes(row.label)).length
                                                }
                                                onChange={handleSelectAllClick}
                                            />
                                        </StyledTableHeader>
                                        {columns.map((col, idx) => (
                                            <StyledTableHeader key={idx}>{col.name}</StyledTableHeader>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tableData
                                        .filter((row) => !removedClusterIds.includes(row.label))
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row) => {
                                            const isItemSelected = isSelected(row.label);
                                            return (
                                                <TableRow
                                                    key={row.label}
                                                    hover
                                                    selected={false}
                                                    sx={{
                                                        backgroundColor: manuallyCheckedRows.includes(String(row.label)) ? '#f5f5f5' : 'inherit',
                                                    }}
                                                >
                                                    <TableCell padding="checkbox">
                                                        <Checkbox checked={isItemSelected} onChange={() => handleClick(row.label)} />
                                                    </TableCell>

                                                    <StyledTableCell
                                                        sx={{
                                                            color: manuallyCheckedRows.includes(String(row.label)) ? '#A0A0A0' : '#005CBC',
                                                            cursor: manuallyCheckedRows.includes(String(row.label)) ? 'default' : 'pointer',
                                                        }}
                                                        onClick={
                                                            manuallyCheckedRows.includes(String(row.label))
                                                                ? undefined
                                                                : () => {
                                                                      setSelectedClusterId(row.label);
                                                                      setSelectedClusterName(row.label);
                                                                      fetchClusterDetail(row.label, row.label);
                                                                  }
                                                        }
                                                    >
                                                        {row.label}
                                                    </StyledTableCell>
                                                    <StyledTableCell>{row.cluster_size}</StyledTableCell>
                                                    <StyledTableCell sx={{ pl: 9 }}>
                                                        {row.selectedImages === undefined ? row.cluster_size : row.selectedImages}
                                                    </StyledTableCell>
                                                    <StyledTableCell align="center">
                                                        {manuallyCheckedRows.includes(String(row.label)) && (
                                                            <Tooltip title="Remove Cluster">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleDeleteCluster(row.label, row.label)}
                                                                    sx={{
                                                                        color: 'primary.main',
                                                                        '&:hover': {
                                                                            backgroundColor: 'rgba(0, 123, 255, 0.1)',
                                                                        },
                                                                    }}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </StyledTableCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Box>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25, 100]}
                                component="div"
                                count={tableData.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </Box>
                    </>
                ) : (
                    <Typography variant="body1" textAlign="center" mt={2}>
                        No Clusters Available
                    </Typography>
                )}
            </Paper>
        </Box>
    );

    const renderContent = () => {
        if (isLoading || isCardLoading) {
            return (
                <Box sx={ProgressIcon.provisioning_wrapper}>
                    <CircularProgress color="inherit" size={16} sx={ProgressIcon.circularProgressColor} />
                    <Typography variant="body2" sx={ProgressIcon.circularProgressColor}>
                        Loading
                    </Typography>
                </Box>
            );
        }

        if (viewMode === 'cards') {
            return (
                <CardList
                    data={cardData}
                    selectedClusterName={selectedClusterName}
                    selectedIds={selectedIds}
                    setSelectedIds={setSelectedIds}
                    selectedClusterId={selectedClusterId}
                    selectedImageCluster={selectedImageCluster}
                    setSelectedImageCluster={setSelectedImageCluster}
                    setSelectedImageCount={setSelectedImageCount}
                    deleteImage={deleteImage}
                    setDeletedImage={setDeletedImage}
                    selectedIdsURL={selectedIdsURL}
                    setSelectedIdsURL={setSelectedIdsURL}
                    selectedCluster={selectedCluster}
                />
            );
        }

        return renderTable();
    };

    return (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Box>{renderContent()}</Box>
            <Dialog
                open={deleteConfirmationOpen}
                onClose={() => setDeleteConfirmationOpen(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">Confirm Removal</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">Are you sure you want to remove cluster {clusterToDelete.name}?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmationOpen(false)} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={confirmDeleteCluster} color="primary" autoFocus>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </ErrorBoundary>
    );
};

export default ClusterLabelTable;
